import { useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router';

import { Campaign } from '@customTypes/campaign';
import { Product } from '@customTypes/product';
import { getOrderTestDisplayLabels } from '@helpers/patient';
import NewReportCardImage from './NewReportCardImage';

interface NewReportCardProps {
  campaign?: Campaign;
  originalRequisitionId: string;
  products: Product[];
}

const MAX_PRODUCTS_TO_SHOW = 4;

export default function NewReportCard(props: NewReportCardProps) {
  const { campaign, originalRequisitionId, products } = props;

  const navigate = useNavigate();
  const params = useParams();
  const [showAll, setShowAll] = useState(false);

  const patientId = params.id ?? '';
  const hiddenProductCount = products.length - MAX_PRODUCTS_TO_SHOW;

  const handleShowAll = () => {
    setShowAll(previousShowAll => !previousShowAll);
  };

  const orderNewReport = () => {
    navigate({
      pathname: `/patients/${patientId}/order-new-report/${originalRequisitionId}`,
      search: campaign ? `?campaign=${campaign.campaign_code}` : '',
    });
  };

  return (
    <Card className='border report-card' bg='white'>
      <Card.Body className='d-flex flex-column align-items-start gap-3 bg-white'>
        <div className='d-flex justify-content-center my-2 w-100 report-logo'>
          <NewReportCardImage campaign={campaign} />
        </div>

        <Button className='w-100' onClick={orderNewReport}>
          Order New Report
        </Button>

        <div className='hr' />

        <div>
          <p className='mb-0 fs-xs fw-bold'>Available product(s):</p>

          <ul className='my-2 report-products'>
            {products.map((product, index) => {
              const isItemHidden = !showAll && index >= MAX_PRODUCTS_TO_SHOW;
              const labels = getOrderTestDisplayLabels({
                product_name: product.name,
                product_family_name: product.product_family_name,
                product_test_name: product.product_test_name,
              });

              return (
                <li key={product.product_id} className={isItemHidden ? 'sr-only fs-xs' : 'fs-xs'}>
                  {labels.headline}
                  {labels.subline ? (
                    <span className='d-block text-body-secondary'>{labels.subline}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {hiddenProductCount > 0 ? (
            <Button variant='link' size='sm' className='ps-0 report-products-toggle' onClick={handleShowAll}>
              {showAll ? 'less' : `(${hiddenProductCount}) more...`}
            </Button>
          ) : null}
        </div>
      </Card.Body>
    </Card>
  );
}
